import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useVendor } from '../contexts/VendorContext';
import API_URL from '../config/api';
import AuthShell, { inputClass, primaryButtonClass, ErrorBox } from '../components/AuthShell';

const Login: React.FC = () => {
  const { t } = useTranslation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { isAuthenticated, isPendingVendor, fetchProfile, loading: vendorLoading } = useVendor();

  useEffect(() => {
    if (!vendorLoading && isAuthenticated) {
      navigate(isPendingVendor ? '/pending' : '/dashboard', { replace: true });
    }
  }, [isAuthenticated, isPendingVendor, vendorLoading, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/token/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: username.trim(), password }),
      });

      if (!response.ok) {
        let errorMessage = t('auth.invalidCredentials');
        try {
          const data = await response.json();
          errorMessage = data.detail || data.error || errorMessage;
        } catch {
          errorMessage = response.statusText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      localStorage.setItem('access_token', data.access);
      localStorage.setItem('refresh_token', data.refresh);
      await fetchProfile();
      // The effect above redirects once the profile is loaded
    } catch (err: any) {
      const msg = String(err?.message || '');
      if (msg.includes('Failed to fetch') || msg.includes('NetworkError') || msg.includes('Network request failed')) {
        setError(t('auth.cannotConnect'));
      } else {
        setError(msg || t('common.genericError'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title={t('auth.signInTitle')} subtitle={t('auth.subtitle')}>
      <form className="space-y-5" onSubmit={handleSubmit}>
        <ErrorBox message={error} />
        <div className="space-y-3">
          <div>
            <label htmlFor="username" className="sr-only">{t('auth.usernameOrEmail')}</label>
            <input
              id="username"
              name="username"
              type="text"
              required
              className={inputClass}
              placeholder={t('auth.usernameOrEmail')}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              autoCapitalize="none"
            />
          </div>
          <div>
            <label htmlFor="password" className="sr-only">{t('auth.password')}</label>
            <input
              id="password"
              name="password"
              type="password"
              required
              className={inputClass}
              placeholder={t('auth.password')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
            />
          </div>
        </div>

        <div className="flex justify-end">
          <Link to="/forgot-password" className="text-sm text-blue-600 active:text-blue-700">
            {t('auth.forgotPassword')}
          </Link>
        </div>

        <button type="submit" disabled={loading} className={primaryButtonClass}>
          {loading ? t('auth.signingIn') : t('auth.signIn')}
        </button>

        <p className="text-center text-sm text-gray-600">
          {t('auth.noAccount')}{' '}
          <Link to="/signup" className="font-medium text-blue-600 active:text-blue-700">
            {t('auth.createAccount')}
          </Link>
        </p>
      </form>
    </AuthShell>
  );
};

export default Login;
